import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { Lightbox } from 'react-modal-image';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { StandaloneSearchBox } from '@react-google-maps/api';

import { AccordionV2 } from './AccordionV2';
import {
  IMAGE_UPLOAD_ERROR,
  LOCATION_ALREADY_EXIST,
  MAX_ALLOWED_LOCATION_IMAGES,
  SELECT_ALL,
  defaultInputData,
  suggestionsDefault,
} from './AddLocationFormV2.config';
import {
  addBtnStyles,
  AmenityName,
  SnackBarContainer,
  CheckboxContainer,
  ImageWrapperContainer,
  UploadedImagesContainer,
  OnDragOverBlock,
  OnDragOverBlockBorder,
  OnDragOverBlockText,
} from './AddLocationFormV2.styles';

import './AddLocationFormV2.scss';

import { ReactComponent as SmallInfoIcon } from '../../../assets/images/small-info-icon.svg';
import CheckBox from '../../../components/common/Checkbox';
import { APPLICATION_HEADERS } from '../../../components/common/headers';
import SnackBar from '../../../components/common/Snackbar/ErrorSnackBar';
import { Block } from '../../../components/common/StyledComponents/StyledBlock';
import { ButtonV2 } from '../../../components/common/v2/ButtonV2/ButtonV2';
import { DatePickerV2 } from '../../../components/common/v2/DatePickerV2/DatePickerV2';
import { FileUploadV2 } from '../../../components/common/v2/FileUploadV2/FIleUploadV2';
import { TextFieldV2 } from '../../../components/common/v2/TextInputV2/TextInputV2';
import UploadedImageBlockComponent from '../../../components/ProviderLocations/NewLocation/UploadedImageBlockComponent';
import { CompressingImageService } from '../../../components/Utils/CompressingImageService';
import { UploadImageService } from '../../../components/Utils/UploadImage';
import { sortAlphabeticalStrings, strToArray } from '../../../components/Utils/Utils';
import { isV2PermissionsEnabled } from '../../../helpers/featureFlags/isV2PermissionsEnabled';
import { getUserRegions } from '../../../helpers/getUserRegions';
import useCouter from '../../../hooks/Couters';
import useDebounce from '../../../hooks/useDebounce';
import useImageUrlValidation from '../../../hooks/useImageUrlValidation';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { useProviderRbac } from '../../../hooks/useProviderRbac';
import { useSyncRef } from '../../../hooks/useSyncRef';
import { loadState } from '../../../store/ProviderLocationsContext';
import { protectApiViaOkta } from '../../../utils/protectApiViaOkta';
import {
  ALLOWED_REGIONS,
  DEFAULT_REGIONS_SHORT,
  getSelectedRegion,
  getSortedRegions,
} from '../helpers/regions';

toast.configure();

export const AddLocationFormV2 = ({
  currentRow,
  isEditOption,
  majorProvidersList,
  onClose,
  onSuccessAddtion,
  onSuccessEditLocation,
  onSuccessEditLocationAlreadyExists,
  providerName,
  userType,
  onRefreshTransactions,
}) => {
  const [isErrorSnackBar, setIsErrorSnackBar] = useState(false);
  const [isErrorSnackBarImageURLs, setIsErrorSnackBarImageURLs] = useState(false);
  const [isResetDatePickerLoc, setIsResetDatePickerLoc] = useState(true);
  const [isSelected, setIsSelected] = useState(false);
  const [isSuccessSnackBar, setIsSuccessSnackBar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onProcessUrls, setOnProcessUrls] = useState(false);

  const [addedCount, setAddedCount] = useState(0);
  const [currencies, setCurrencies] = useState({});
  const [fixedAmenitiesList, setFixedAmenitiesList] = useState([]);
  const [imageError, setImageError] = useState('');
  const [suggestions, setSuggestions] = useState(suggestionsDefault);
  const [values, setValues] = useState(defaultInputData);

  const addressRef = useRef(null);
  const formRef = useRef(null);
  const inputRef = useRef();

  const { inventoryRecordupdateCounter, inventoryDataUploadCounter } = useCouter();
  const regionsV2 = useMemo(() => getUserRegions(), []);
  const regions = isV2PermissionsEnabled() ? regionsV2 : DEFAULT_REGIONS_SHORT;
  const sortedRegion = useMemo(() => getSortedRegions(regions), [regions]);

  useEffect(() => {
    const storedState = loadState();

    storedState?.fixedAmenitiesList?.length > 0 &&
      setFixedAmenitiesList(storedState?.fixedAmenitiesList);

    storedState?.currenciesList &&
      Object.keys(storedState?.currenciesList)?.length > 0 &&
      setCurrencies(storedState?.currenciesList);
  }, []);

  const SELECTED_AMENITIES = useMemo(
    () =>
      fixedAmenitiesList?.length
        ? [
            { name: SELECT_ALL, selected: false },
            ...fixedAmenitiesList.map((amenity) => ({
              name: amenity?.name,
              selected: false,
            })),
          ]
        : [],
    [fixedAmenitiesList],
  );

  const [amenitiesData, setAmenitiesData] = useState(SELECTED_AMENITIES);

  if (!!SELECTED_AMENITIES?.length > 0 && amenitiesData?.length === 0) {
    setAmenitiesData(SELECTED_AMENITIES);
  }

  const schema = useMemo(() => {
    const schemaObj = yup.object().shape({
      provider: yup.string().required('Provider Name is required'),
      emailid: yup
        .string()
        .nullable(true)
        .notRequired()
        .test('emailid is invalid', 'Please enter valid email(s)', (value) => {
          if (!value) return true;

          const emails = value.split(',').map((email) => email.trim());
          const emailRegex = /^(?!\d+@)\w+([-+.']\w+)*@(?!\d+\.)\w+([-.]\w+)*\.\w+([-.]\w+)*$/;

          return emails.every((email) => emailRegex.test(email));
        }),
      address: !isEditOption
        ? yup.string().required('Address is required')
        : yup.string().nullable(true).notRequired(),
      city: !isEditOption
        ? yup.string().required('City is required')
        : yup.string().nullable(true).notRequired(),
      state: yup.string().nullable(true).notRequired(),
      zipCode: !isEditOption
        ? yup.string().required('Zip Code is required')
        : yup.string().nullable(true).notRequired(),
      country: !isEditOption
        ? yup.string().required('Country is required')
        : yup.string().nullable(true).notRequired(),
      region: !isEditOption
        ? yup.string().required('Region is required')
        : yup.string().nullable(true).notRequired(),
      currency: yup.string(),
      price: yup
        .string()
        .nullable(true)
        .notRequired()
        .test('is-integer', 'Price must be a positive number or an integer', (value) => {
          if (!value) {
            return true;
          }

          return /^[1-9]$|^[1-9][0-9]*$/.test(value);
        }),
      selectedDate: yup.string(),
      amneties: yup.string(),
      images: yup.string(),
    });

    return schemaObj;
  }, [isEditOption]);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    formState: { errors },
    clearErrors,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      provider: '',
      emailid: '',
      address: '',
      formatted_address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      lat: '',
      long: '',
      region: '',
      currency: '',
      price: '',
      selectedDate: '',
      amneties: '',
      images: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!loading && !isResetDatePickerLoc) {
      setIsResetDatePickerLoc(true);
    }
  }, [isResetDatePickerLoc, loading]);

  useEffect(() => {
    if (isEditOption) {
      Object.keys(currentRow).forEach((fieldName) => {
        if (fieldName === 'provider_name') {
          setValue('provider', currentRow[fieldName]);
        }
        if (fieldName === 'provider_contact_info') {
          const emails = currentRow[fieldName] && currentRow[fieldName]?.map((obj) => obj.email);

          setValue('emailid', emails.join(', '));
        }
        if (fieldName === 'formatted_address') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'address') {
          if (currentRow[fieldName]) {
            setValue(fieldName, currentRow[fieldName]);
          } else {
            setValue(fieldName, currentRow['formatted_address']);
          }
        }
        if (fieldName === 'city') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'state') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'country') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'zipPostal') {
          setValue('zipCode', currentRow[fieldName]);
        }
        if (fieldName === 'region') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'provider_id') {
          setValue(fieldName, currentRow[fieldName]);
        }
        if (fieldName === 'floor_pricing' && !!currentRow?.floor_pricing) {
          setValue('currency', currentRow?.floor_pricing['pricing_currency']);
          setValue('price', currentRow?.floor_pricing['pricing_1_month_term']);
        }
        if (fieldName === 'availabilityDate') {
          setValue(fieldName, moment(currentRow[fieldName]).format('DD-MMM-YYYY'));
          setValue('selectedDate', moment(currentRow[fieldName]).format('DD-MMM-YYYY'));
        }
      });
    }
  }, [currentRow, setValue, isEditOption]);

  const onSubmit = useCallback(
    async (data) => {
      setLocationAlreadyExists(false);
      const url = `${process.env.REACT_APP_MK2_URL}/providerlocation`;
      let reqData = {};
      const userDataStore = JSON.parse(sessionStorage.getItem('USER_DATA'));

      if (!isEditOption) {
        reqData = {
          provider_name: data?.provider,
          address: data?.address,
          formatted_address: data?.formatted_address,
          lng: data?.long,
          lat: data?.lat,
          email: data?.emailid,
          floor_amenities: data?.amneties,
          suite_amenities: data?.amneties,
          region: data?.region,
          city: data?.city,
          state: data?.state,
          zipPostal: data?.zipCode,
          country: data?.country,
          floor_pricing: { pricing_currency: data?.currency, pricing_1_month_term: data?.price },
          availabilityDate: moment(data.selectedDate).isValid()
            ? moment(data.selectedDate).format('YYYY-MM-DD')
            : '',
          picture_path: data.images,

          status: 'Active',
          source_system: ['RESEARCH'],
          coordinates: {
            Latitude: data?.lat,
            Longitude: data?.long,
          },
          soft_delete_summary: {
            reason: '',
            is_softdeleted: 'no',
          },
          provider_contact_info: [
            {
              last_name: '',
              phone_number: '',
              first_name: '',
              email: data?.emailid,
            },
          ],
          property_id: {
            baidu_uid: '',
            research_property_id: '',
            google_place_id: '',
          },
          createdBy: userDataStore['User email'],
        };
      } else {
        const isFormattedAddressChanged = () => {
          return currentRow['formatted_address'] !== data['formatted_address'];
        };

        const isValuesAreChanged = (val) => {
          return currentRow[val] !== data[val] && data[val] !== null;
        };

        reqData = {
          provider_id: data?.provider_id,
          updatedBy: userDataStore['User email'],
          provider_name: data?.provider,
          provider_contact_info: [
            {
              last_name: '',
              phone_number: '',
              first_name: '',
              email: data?.emailid,
            },
          ],
          floor_pricing: { pricing_currency: data?.currency, pricing_1_month_term: data?.price },
          picture_path: data.images,
          floor_amenities: data?.amneties,
          suite_amenities: data?.amneties,
          availabilityDate: moment(data.selectedDate).isValid()
            ? moment(data.selectedDate).format('YYYY-MM-DD')
            : '',
          building_id: currentRow['building_id'],
          floor_id: currentRow['floor_id'],
          suite_id: currentRow['suite_id'],
        };

        const fieldsToCheckAddress = ['city', 'state', 'country', 'region'];

        if (isFormattedAddressChanged()) {
          reqData['formatted_address'] = data['formatted_address'];
        }

        if (isFormattedAddressChanged() && isValuesAreChanged('address')) {
          reqData['address'] = data['address'];
        }

        fieldsToCheckAddress.forEach((field) => {
          if (isValuesAreChanged(field)) {
            reqData[field] = data[field];
          }
        });

        if (currentRow['zipPostal'] !== data['zipCode'] && data['zipCode'] !== null) {
          reqData['zipPostal'] = data['zipCode'];
        }
      }

      if (!moment(data.selectedDate).isValid()) {
        delete reqData.availabilityDate;
      }

      setLoading(true);

      try {
        let data = null;

        if (isEditOption) {
          ({ data } = await protectApiViaOkta('put', url, reqData));

          inventoryRecordupdateCounter();
        } else {
          ({ data } = await protectApiViaOkta('post', url, reqData));

          inventoryDataUploadCounter();
        }

        if (isEditOption) {
          if (data?.locationAlreadyExists) {
            onSuccessEditLocationAlreadyExists();
          } else {
            onSuccessEditLocation();
          }
        }

        setAddedCount(1);
        setAmenitiesData(SELECTED_AMENITIES);
        setIsSuccessSnackBar(true);
        if (data?.locationAlreadyExists) {
          setLocationAlreadyExists(true);
        }
        setCurrencySymbol('');
        setUploadedImages([]);
        if (!isEditOption) onSuccessAddtion();

        onRefreshTransactions?.();
      } catch (e) {
        console.error('Occured error while trying to add custom option - ', e);
        setIsErrorSnackBar(true);
      } finally {
        setValue('selectedDate', '');
        setIsResetDatePickerLoc(false);
        setLoading(false);
        onClose();
        reset();
        setValue('provider', providerName);
      }
    },
    [
      isEditOption,
      currentRow,
      SELECTED_AMENITIES,
      inventoryRecordupdateCounter,
      inventoryDataUploadCounter,
      onSuccessAddtion,
      onSuccessEditLocationAlreadyExists,
      onSuccessEditLocation,
      setValue,
      onClose,
      reset,
      userType,
      providerName,
    ],
  );

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errors]);

  const debouncedAddress = useDebounce(values.address, 400);

  useEffect(() => {
    setValue('provider', providerName);
  }, [userType, providerName, setValue]);

  const sortedCurrencies = useMemo(() => {
    const sorted = Object.entries(currencies ?? {}).sort((a, b) => {
      // Check if either currency is USD and prioritize it
      if (a[0]?.toLowerCase() === 'usd') return -1;
      if (b[0]?.toLowerCase() === 'usd') return 1;

      // If neither currency is USD, perform alphabetical sorting
      const firstCurrency = a[0]?.toLowerCase();
      const secondCurrency = b[0]?.toLowerCase();

      // USD currency should be at the top of the list
      if (firstCurrency.toLowerCase() === 'usd') return -1;
      if (firstCurrency > secondCurrency) return 1;
      if (firstCurrency < secondCurrency) return -1;

      return 0;
    });

    return sorted;
  }, [currencies]);

  const getAddressComponent = useCallback((components, type) => {
    const result = components.find((component) => component.types.includes(type));

    return result ? result.long_name : '';
  }, []);

  const handlePlaceChanged = useCallback(() => {
    const [place] = inputRef.current.getPlaces();

    if (place) {
      let { city, state, zipCode, country, region } = ''; // Onchange address input, need to clear the existing values of address input fields.
      const { address_components } = place;

      const countryAbbr = address_components.find((component) =>
        component.types.includes('country'),
      )?.short_name;
      const allowedCountries = regions
        .flatMap((r) => ALLOWED_REGIONS[r] || [])
        .map((c) => c.toUpperCase());

      if (!allowedCountries.includes(countryAbbr)) {
        setValue('address', '');
        setError('address', {
          type: 'manual',
          message: `Selected address is not found in ${[...regions].join(', ')} ${
            regions.length > 1 ? 'regions' : 'region'
          }. `,
        });

        setValue('formatted_address', '');
        setValue('lat', '');
        setValue('long', '');
        setValue('city', '');
        setValue('state', '');
        setValue('zipCode', '');
        setValue('country', '');
        setValue('region', '');

        return;
      }

      city = getAddressComponent(address_components, 'locality');
      state = getAddressComponent(address_components, 'administrative_area_level_1');
      zipCode = getAddressComponent(address_components, 'postal_code');
      country = getAddressComponent(address_components, 'country');
      region = getSelectedRegion(countryAbbr);
      setValue('address', place.name);
      setValue('formatted_address', place.formatted_address);
      setValue('lat', place.geometry.location.lat());
      setValue('long', place.geometry.location.lng());
      setValue('city', city);
      setValue('state', state);
      setValue('zipCode', zipCode);
      setValue('country', country);
      setValue('region', region);
      clearErrors(['address', 'city', 'state', 'zipCode', 'country', 'region']);
    }
  }, [regions, getAddressComponent, setValue, clearErrors, setError]);

  const handleCloseWindow = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      reset();
      setValue('selectedDate', '');
      setIsResetDatePickerLoc(false);
      setAmenitiesData(SELECTED_AMENITIES);
      setCurrencySymbol('');
      setUploadedImages([]);
      setSuggestions({
        selected: {},
        suggestionsData: {},
        suggestionsListOpened: false,
      });
      setLoading(false);
      onClose(addedCount);
      setValue('provider', providerName);
    },
    [addedCount, reset, setValue, SELECTED_AMENITIES, userType, providerName, onClose],
  );

  const handleCloseDropdown = useCallback(() => {
    setSuggestions((prev) => ({ ...prev, suggestionsListOpened: false }));

    if (values?.address?.trim() === '') return;

    if (suggestions.selected?.place_name && suggestions.selected?.place_name !== values.address) {
      setValues((prev) => ({
        ...prev,
        address: suggestions.selected?.place_name,
      }));
      setIsSelected(true);
    }
  }, [suggestions.selected, values.address, setValues, setSuggestions, setIsSelected]);

  useOutsideClick(addressRef, handleCloseDropdown);

  useEffect(() => {
    if (isSuccessSnackBar) {
      onClose();
      setValues(defaultInputData);
      setSuggestions(suggestionsDefault);
    }
  }, [isSuccessSnackBar, onClose]);

  const isSelectedRef = useSyncRef(isSelected);

  useEffect(() => {
    if (!debouncedAddress) {
      handleCloseDropdown();

      return;
    }

    if (isSelectedRef.current) {
      setIsSelected(false);

      return;
    }
  }, [debouncedAddress, isSelectedRef, handleCloseDropdown]);

  const [uploadingProgress] = useState(0);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);
  const [isCurrentImageModalOpen, setIsCurrentImageModalOpen] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [urlsListLinks, setUrlsListLinks] = useState([]);
  const [dragOverImages, setDragOverImages] = useState({
    floorPlan: false,
    locationPlan: false,
  });
  const [isHoveredImage, setIsHoveredImage] = useState({});
  const [locationAlreadyExists, setLocationAlreadyExists] = useState(false);

  const validationResults = useImageUrlValidation(urlsListLinks);

  const updateImagesPostData = useCallback(
    (imagesArray) => {
      const imageURLsCommaSeperate = imagesArray
        ?.filter((imgObj) => imgObj?.imgURL)
        .map((imgObj) => imgObj?.imgURL)
        .join(',');

      setValue('images', imageURLsCommaSeperate);
    },
    [setValue],
  );

  const setAmenitiesDataCB = useCallback(
    (Amenities) => {
      setAmenitiesData((prevAmenitiesData) => {
        let newAmenitiesData = [...Amenities];

        newAmenitiesData = newAmenitiesData.map((amenity) => {
          if (amenity) {
            return {
              name: amenity.name,
              selected: amenity.selected,
            };
          }

          return amenity;
        });

        setValue(
          'amneties',
          newAmenitiesData
            .filter((amn) => amn?.selected)
            .map((amn) => amn?.name)
            .join(','),
        );

        return newAmenitiesData;
      });
    },
    [setValue],
  );

  // only single time code executed for Edit location

  useEffect(() => {
    if (!isEditOption || fixedAmenitiesList?.length === 0 || !currentRow['floor_amenities']) {
      return;
    }

    const defaultAmen = fixedAmenitiesList?.length
      ? [
          { name: SELECT_ALL, selected: false },
          ...fixedAmenitiesList.map((amenity) => ({
            name: amenity?.name,
            selected: false,
          })),
        ]
      : [];

    const strAmentiesConverssion = strToArray(currentRow['floor_amenities']);

    const finalAmenetiesArray = defaultAmen.map((obj, index) => {
      if (strAmentiesConverssion.includes(obj?.name)) {
        obj.selected = true;
      }

      return obj;
    });

    setAmenitiesDataCB(finalAmenetiesArray);
  }, [
    fixedAmenitiesList,
    currentRow,
    isEditOption,
    setAmenitiesDataCB,
    fixedAmenitiesList?.length,
  ]);

  useEffect(() => {
    if (!isEditOption) return;

    const strImageConverssion = strToArray(currentRow['picture_path']);

    if (strImageConverssion?.length > 0) {
      const changeImagesAssr = strImageConverssion
        ?.filter((obj) => {
          return !!obj;
        })
        .map((obj) => {
          return {
            imgURL: obj,
            url: obj,
            isUploading: false,
            isCompleted: true,
          };
        });

      setUploadedImages(changeImagesAssr);
      const imageURLsCommaSeperate = changeImagesAssr?.map((imgObj) => imgObj?.imgURL).join(',');

      setValue('images', imageURLsCommaSeperate);
    }
  }, [currentRow, isEditOption, setValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (validationResults?.length > 0) {
        const validUrlsList = validationResults
          ?.filter((obj) => {
            return !!obj.valid;
          })
          .map((obj) => {
            return {
              imgURL: obj?.url,
              ...obj,
              isUploading: false,
              isCompleted: true,
            };
          });

        setUploadedImages((prev) => {
          const concatenateTwoArray = [...prev, ...validUrlsList];

          updateImagesPostData(concatenateTwoArray);

          return concatenateTwoArray;
        });
        setUrlsListLinks([]);

        const unvalidURLSCount = validationResults?.length - validUrlsList?.length;

        if (!!unvalidURLSCount && unvalidURLSCount > 0) {
          setIsErrorSnackBarImageURLs(true);
          setImageError(
            `${unvalidURLSCount} out of ${validationResults.length} images uploaded do not have valid image urls. Please verify and upload again.`,
          );
        }
      }
      setUrlsText('');
      setOnProcessUrls(false);
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, [validationResults, updateImagesPostData]);

  const handleUploadImages = useCallback(
    async (e, type) => {
      const { errorsLength, images, errors } = UploadImageService.validateEventImages(e);

      if (errors?.length > 0) {
        setIsErrorSnackBarImageURLs(true);
        setImageError(`${errors[0]}`);
      }

      if (errorsLength !== 0 || images?.length === 0) return;

      if (
        images.length > MAX_ALLOWED_LOCATION_IMAGES ||
        uploadedImages?.length + images.length > MAX_ALLOWED_LOCATION_IMAGES
      ) {
        setIsErrorSnackBarImageURLs(true);

        setImageError(
          `You can upload only ${MAX_ALLOWED_LOCATION_IMAGES} images for location images`,
        );
      }

      const compressingService = new CompressingImageService();
      const modifiedFields = images.map((file) => {
        compressingService.addImage(file);

        return { url: file };
      });

      setUploadedImages((prev) => [...prev, ...modifiedFields]);

      const formData = await compressingService.getCompressedImages();

      await UploadImageService.upload(formData, {
        onSuccess: (response) => {
          let resultImages;

          if (Object.keys(response?.data)?.length > 0) {
            resultImages = Object.entries(response.data).map(([name, url]) => {
              return { url };
            });
          }
          setUploadedImages((prev) => {
            const updatedData = prev.map((item) => {
              const match = resultImages?.find((obj) => {
                const isMatch =
                  obj?.url?.split('_')?.slice(1)[0] ===
                  item?.url?.name?.replace(/[^a-zA-Z0-9.]/g, '');

                return isMatch;
              });
              let newItem = { ...item };

              if (!newItem?.isCompleted) {
                newItem = { ...newItem, isUploading: false, isCompleted: true };
              }

              if (match) {
                newItem = { ...newItem, imgURL: match?.url };
              }

              return newItem;
            });

            updateImagesPostData(updatedData);

            return updatedData;
          });
        },
        onFail: (msg) => {
          if (msg && msg.length > 0) {
            setUploadedImages((prev) => {
              return prev.filter((obj) => {
                return obj?.isCompleted;
              });
            });
            setIsErrorSnackBarImageURLs(true);
            setImageError(IMAGE_UPLOAD_ERROR);
          } else {
            setIsErrorSnackBarImageURLs(true);
            setImageError(`Something went wrong`);
          }
        },
      });
    },
    [updateImagesPostData, uploadedImages?.length],
  );

  const handleDeleteImage = useCallback(
    (e, index, url) => {
      e.stopPropagation();
      setUploadedImages((prev) => {
        const updatedData = prev.filter(
          (item, itemIndex) => !(item?.imgURL === url && index === itemIndex),
        );

        updateImagesPostData(updatedData);

        return updatedData;
      });
      setIsHoveredImage((prev) => ({ ...prev, [index]: false }));
    },
    [updateImagesPostData],
  );

  const handleImageClick = useCallback(
    (e, index) => {
      e.stopPropagation();
      setCurrentImage(uploadedImages[index]?.imgURL);
      setIsCurrentImageModalOpen(true);
    },
    [uploadedImages],
  );

  const handleDrag = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragOverImages((prev) => ({ ...prev, [type]: true }));
    }

    if (e.type === 'dragleave') {
      setDragOverImages((prev) => ({ ...prev, [type]: false }));
    }
  }, []);

  const handleChangeURL = useCallback((e) => {
    const newUrls = e.target?.value.split(',').map((url) => {
      return { url: url.trim() };
    });

    if (newUrls?.length > 0) {
      setUrlsListLinks((prev) => [...prev, ...newUrls]);
      setUrlsText('Validating image URLs...');
      setOnProcessUrls(true);
    }
  }, []);

  const handleChangeAmenity = useCallback(
    (amenityName) => {
      setAmenitiesData((prevAmenitiesData) => {
        let newAmenitiesData = [...prevAmenitiesData];

        if (amenityName === SELECT_ALL) {
          const allAmenities = newAmenitiesData.find((amenity) => amenity.name === SELECT_ALL);

          if (allAmenities) {
            newAmenitiesData = newAmenitiesData.map((amenity) => ({
              ...amenity,
              selected: !allAmenities.selected,
            }));
          }
        } else {
          newAmenitiesData = newAmenitiesData.map((amenity) => {
            if (
              amenity &&
              amenity.name &&
              amenity.name.toLowerCase().trim() === amenityName.toLowerCase().trim()
            ) {
              return {
                ...amenity,
                selected: !amenity.selected,
              };
            }

            return amenity;
          });

          const isAllSelected = newAmenitiesData
            .filter((amenity) => amenity.name !== SELECT_ALL)
            .every((amenity) => amenity.selected);

          newAmenitiesData = newAmenitiesData.map((amenity) => {
            if (amenity.name === SELECT_ALL) {
              return {
                ...amenity,
                selected: isAllSelected,
              };
            }

            return amenity;
          });
        }
        setValue(
          'amneties',
          newAmenitiesData
            .filter((amn) => amn?.selected && amn?.name !== SELECT_ALL)
            .map((amn) => amn?.name)
            .join(','),
        );

        return newAmenitiesData;
      });
    },
    [setValue],
  );

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') event.preventDefault();
  }, []);

  const handleFieldFocus = useCallback((fieldName) => clearErrors(fieldName), [clearErrors]);
  const handleFieldBlur = useCallback((fieldName) => trigger(fieldName), [trigger]);

  const handlePriceChange = useCallback(
    (event) => {
      let cleanValue = event?.target?.value.replace(/[^0-9]/g, '');

      cleanValue = cleanValue.replace(/^0+/, '');

      setValue('price', cleanValue);
    },
    [setValue],
  );

  const currencyHandleChange = useCallback(
    (event) => {
      const targetString = event.target?.value;
      const result =
        sortedCurrencies?.find((subArray) => subArray[0] === targetString)?.[1] ?? null;
      const lastCharacter = result?.trim()?.split('-').pop()?.trim() ?? null;

      !!lastCharacter && setCurrencySymbol(lastCharacter);
    },
    [sortedCurrencies, setCurrencySymbol],
  );

  const handleMouseEnterUploadedImage = useCallback(
    (index) => setIsHoveredImage((prev) => ({ ...prev, [index]: true })),
    [],
  );
  const handleMouseLeaveUploadedImage = useCallback(
    (index) => setIsHoveredImage((prev) => ({ ...prev, [index]: false })),
    [],
  );

  const uploadedImagesElements = useMemo(() => {
    return uploadedImages?.map((image, index) => (
      <UploadedImageBlockComponent
        key={`${image?.url}-${index}`}
        index={index}
        image={image}
        isHoveredImage={isHoveredImage[index]}
        handleMouseEnterUploadedImage={handleMouseEnterUploadedImage}
        handleMouseLeaveUploadedImage={handleMouseLeaveUploadedImage}
        handleDeleteImage={handleDeleteImage}
        progressImageUpload={uploadingProgress}
        handleImageClick={handleImageClick}
      />
    ));
  }, [
    uploadedImages,
    handleMouseEnterUploadedImage,
    handleMouseLeaveUploadedImage,
    handleDeleteImage,
    isHoveredImage,
    uploadingProgress,
    handleImageClick,
  ]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <ButtonV2 disabled={loading} variant="contained" sx={addBtnStyles} type="submit">
            {loading ? (
              <CircularProgress size={20} />
            ) : isEditOption ? (
              'Update Location'
            ) : (
              'Add Location'
            )}
          </ButtonV2>
          <IconButton onClick={handleCloseWindow}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box mb={3}>
          <Typography variant="h3" fontSize="40px" fontFamily="Financier" fontWeight={600}>
            {isEditOption ? 'Edit' : 'Add New'} Location
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={4} mb={4}>
          <Controller
            name="provider"
            control={control}
            render={({ field: { onBlur, onChange, value } }) => (
              <Autocomplete
                freeSolo
                options={sortAlphabeticalStrings('providerName', majorProvidersList)}
                value={providerName}
                disabled={!!providerName}
                getOptionLabel={(option) =>
                  typeof option === 'object' ? option.providerName : option
                }
                onInputChange={(_, newValue) => {
                  onChange(newValue || '');
                }}
                onFocus={() => handleFieldFocus('provider')}
                onBlur={onBlur}
                renderInput={(params) => (
                  <TextFieldV2
                    {...params}
                    error={Boolean(errors.provider)}
                    helperText={errors.provider ? errors.provider.message : ''}
                    variant="outlined"
                    fullWidth
                    label="Provider Name*"
                    onKeyDown={handleKeyDown}
                  />
                )}
                clearIcon={null}
                popupIcon={null}
              />
            )}
          />
          <div>
            <Controller
              name="emailid"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  error={Boolean(errors.emailid)}
                  helperText={errors.emailid ? errors.emailid.message : ''}
                  variant="outlined"
                  fullWidth
                  label="Email(s)"
                  onFocus={() => handleFieldFocus('emailid')}
                  onBlur={() => handleFieldBlur('emailid')}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={{
                    shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                  }}
                />
              )}
            ></Controller>
            <Typography
              fontSize="12px"
              color="secondary.dark"
              display="flex"
              alignItems="center"
              gap={0.5}
            >
              <SmallInfoIcon /> Commas can be used to separate multiple emails.
            </Typography>
          </div>
        </Box>
        <Divider />

        <Box mt={4} mb={4}>
          <Typography variant="h4" fontSize="32px" fontFamily="Financier" fontWeight={600}>
            Address
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StandaloneSearchBox
              onLoad={(ref) => (inputRef.current = ref)}
              onPlacesChanged={handlePlaceChanged}
            >
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextFieldV2
                    {...field}
                    autoComplete="address"
                    error={Boolean(errors.address)}
                    helperText={errors.address ? errors.address.message : ''}
                    variant="outlined"
                    fullWidth
                    label={isEditOption ? 'Address' : 'Address*'}
                    onFocus={() => handleFieldFocus('address')}
                    onBlur={() => handleFieldBlur('address')}
                    placeholder=""
                    onKeyDown={handleKeyDown}
                    InputLabelProps={{
                      shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                    }}
                  />
                )}
              />
            </StandaloneSearchBox>
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  error={Boolean(errors.city)}
                  helperText={errors.city ? errors.city.message : ''}
                  variant="outlined"
                  fullWidth
                  label={isEditOption ? 'City' : 'City*'}
                  onFocus={() => handleFieldFocus('city')}
                  onBlur={() => handleFieldBlur('city')}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={{
                    shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  error={Boolean(errors.state)}
                  helperText={errors.state ? errors.state.message : ''}
                  variant="outlined"
                  fullWidth
                  label="State"
                  onFocus={() => handleFieldFocus('state')}
                  onBlur={() => handleFieldBlur('state')}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={{
                    shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  error={Boolean(errors.zipCode)}
                  helperText={errors.zipCode ? errors.zipCode.message : ''}
                  variant="outlined"
                  fullWidth
                  label={isEditOption ? 'Zip Code' : 'Zip Code*'}
                  onFocus={() => handleFieldFocus('zipCode')}
                  onBlur={() => handleFieldBlur('zipCode')}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={{
                    shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  error={Boolean(errors.country)}
                  helperText={errors.country ? errors.country.message : ''}
                  variant="outlined"
                  fullWidth
                  label={isEditOption ? 'Country' : 'Country*'}
                  onFocus={() => handleFieldFocus('country')}
                  onBlur={() => handleFieldBlur('country')}
                  onKeyDown={handleKeyDown}
                  InputLabelProps={{
                    shrink: (field.value !== '' && field.value !== null) || field.onFocus,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="region"
              control={control}
              render={({ field }) => (
                <TextFieldV2
                  {...field}
                  select
                  error={Boolean(errors.region)}
                  helperText={errors.region ? errors.region.message : ''}
                  variant="outlined"
                  onFocus={() => handleFieldFocus('region')}
                  fullWidth
                  // label="Region"
                  label={isEditOption ? 'Region' : 'Region*'}
                  onKeyDown={handleKeyDown}
                >
                  {sortAlphabeticalStrings('name', sortedRegion)?.map((option) => (
                    <MenuItem
                      className="muiCustomMenuStyles"
                      key={option.region}
                      value={option.region}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </TextFieldV2>
              )}
            />
          </Grid>
        </Grid>
        <Box mt={3}>
          <AccordionV2
            defaultIsOpen={isEditOption}
            title="Additional Information"
            id="additionalInformationGroup"
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="currency"
                  control={control}
                  render={({ onChange, field }) => (
                    <TextFieldV2
                      {...field}
                      select
                      variant="outlined"
                      fullWidth
                      error={Boolean(errors.currency)}
                      helperText={errors.currency ? errors.currency.message : ''}
                      label="Currency"
                      onChange={(e) => {
                        field.onChange(e);
                        currencyHandleChange(e);
                      }}
                      onKeyDown={handleKeyDown}
                    >
                      {sortedCurrencies?.map(([currency, name], index) => {
                        return (
                          <MenuItem className="muiCustomMenuStyles" key={index} value={currency}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </TextFieldV2>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <TextFieldV2
                      {...field}
                      error={Boolean(errors.price)}
                      value={getValues().price}
                      helperText={errors.price ? errors.price.message : ''}
                      variant="outlined"
                      fullWidth
                      label="Price Per Month For 1 Month Term"
                      onChange={handlePriceChange}
                      onKeyDown={(e) => {
                        handleKeyDown(e);
                      }}
                      onFocus={() => handleFieldFocus('price')}
                      onBlur={() => handleFieldBlur('price')}
                      InputProps={{
                        startAdornment:
                          currencySymbol && currencySymbol.trim() !== '' ? (
                            <InputAdornment position="start">
                              <Typography variant="body1">{currencySymbol}</Typography>
                            </InputAdornment>
                          ) : null,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  key={isResetDatePickerLoc}
                  name="selectedDate"
                  control={control}
                  render={() => (
                    <DatePickerV2
                      label="Availability Date"
                      handleChange={(date) => {
                        setValue('selectedDate', date);
                      }}
                      disablePast
                      inputFormat="DD-MMM-YYYY"
                      dateFormat="YYYY-MM-DD"
                      selectedDate={getValues()?.selectedDate}
                      error={Boolean(errors.selectedDate)}
                      helperText={errors.selectedDate ? errors.selectedDate.message : ''}
                      onKeyDown={handleKeyDown}
                      isResetDatePickerLoc
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box mt={2} mb={2}>
              <Typography variant="h5" fontFamily="Financier" fontWeight={600}>
                Amenities
              </Typography>
            </Box>

            <CheckboxContainer>
              {amenitiesData?.map((amenity, index) => {
                return (
                  <Block display="inline-flex" gap="20" key={index}>
                    <CheckBox
                      checked={amenity.selected}
                      onChange={() => handleChangeAmenity(amenity.name)}
                    />
                    <AmenityName>{amenity.name}</AmenityName>
                  </Block>
                );
              })}
            </CheckboxContainer>

            <Box mt={2} mb={2}>
              <Typography variant="h5" fontFamily="Financier" fontWeight={600}>
                Location Images
              </Typography>
            </Box>

            <ImageWrapperContainer>
              <FileUploadV2 onUpload={(e) => handleUploadImages(e)} addNewLocation="true" />

              {dragOverImages?.locationPlan && (
                <OnDragOverBlock
                  onDragOver={(e) => handleDrag(e, 'locationPlan')}
                  onDragLeave={(e) => handleDrag(e, 'locationPlan')}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUploadImages(e, 'locationImages');
                    setDragOverImages((prev) => ({
                      ...prev,
                      locationPlan: false,
                    }));
                  }}
                >
                  <OnDragOverBlockBorder>
                    <OnDragOverBlockText>Drop images to upload</OnDragOverBlockText>
                  </OnDragOverBlockBorder>
                </OnDragOverBlock>
              )}
            </ImageWrapperContainer>

            <div
              style={{
                display: 'grid',
                textAlign: 'center',
                margin: '10px 0',
              }}
            >
              OR
            </div>

            <Box>
              <TextFieldV2
                id="urls"
                variant="outlined"
                value={urlsText}
                className={onProcessUrls ? 'hashTextValidating' : ''}
                onChange={handleChangeURL}
                fullWidth
                label="Image URLs"
                onKeyDown={handleKeyDown}
              />
              <Typography
                fontSize="12px"
                color="secondary.dark"
                display="flex"
                alignItems="center"
                gap={0.5}
              >
                <SmallInfoIcon /> Commas can be used to separate multiple emails.
              </Typography>
            </Box>

            <UploadedImagesContainer>{uploadedImagesElements}</UploadedImagesContainer>
          </AccordionV2>
        </Box>
      </form>
      <SnackBarContainer>
        {isErrorSnackBar && (
          <SnackBar
            title={imageError}
            onClose={() => setIsErrorSnackBar(false)}
            delay={5000}
            isError
          />
        )}
        {isSuccessSnackBar && !locationAlreadyExists && (
          <SnackBar
            title="Location added successfully"
            onClose={() => setIsSuccessSnackBar(false)}
            delay={5000}
            customClass="snackBarBlock"
          />
        )}

        {isSuccessSnackBar && locationAlreadyExists && (
          <SnackBar
            title={LOCATION_ALREADY_EXIST}
            onClose={() => setIsSuccessSnackBar(false)}
            delay={8000}
            isError
            customClass="snackBarBlock"
          />
        )}
        {isErrorSnackBarImageURLs && (
          <SnackBar
            title={imageError}
            onClose={() => setIsErrorSnackBarImageURLs(false)}
            delay={8000}
            isError
            customClass="snackBarBlock"
          />
        )}
      </SnackBarContainer>
      <Block>
        {isCurrentImageModalOpen && (
          <Lightbox medium={currentImage} onClose={() => setIsCurrentImageModalOpen(false)} />
        )}
      </Block>
    </>
  );
};
